// Service de tracking avancé des vues de tableaux
import sessionManager from './sessionManager.js';
import ErrorHandler from './errorHandler.js';

const API_BASE_URL = process.env.REACT_APP_API_URL || (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3001');
const VIEW_DURATION_THRESHOLD = 3000; // 3 secondes minimum pour compter une vue
const INTERACTION_DEBOUNCE = 500; // Délai pour le debounce des interactions

class ViewTracker {
  constructor() {
    this.currentView = null;
    this.viewStartTime = null;
    this.interactions = 0;
    this.maxScrollDepth = 0;
    this.interactionTimer = null;
    this.viewQueue = [];
    this.isProcessing = false;
  }

  /**
   * Démarre le tracking d'une vue de tableau
   */
  async startView(tableauId, metadata = {}) {
    // Terminer la vue précédente si elle existe
    if (this.currentView) {
      this.endView();
    }

    // Attendre que sessionManager soit initialisé
    try {
      await sessionManager.waitForInit();
    } catch (error) {
      console.error('⚠️ Erreur lors de l\'attente d\'initialisation du SessionManager:', error);
    }

    // Récupérer le sessionId et visitorId
    const sessionId = sessionManager.getSessionId();
    const visitorId = sessionManager.getVisitorId();

    // Si toujours pas de sessionId, créer un sessionId temporaire
    if (!sessionId) {
      console.error('❌ SessionManager non initialisé après waitForInit, création sessionId temporaire');
      const tempSessionId = `temp_session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const tempVisitorId = `temp_visitor_${Date.now()}`;

      this.currentView = {
        tableauId: parseInt(tableauId),
        sessionId: tempSessionId,
        visitorId: tempVisitorId,
        startTime: Date.now(),
        interactions: 0,
        scrollDepth: 0,
        cameFrom: document.referrer || window.location.pathname,
        metadata: {
          ...metadata,
          viewport: `${window.innerWidth}x${window.innerHeight}`,
          devicePixelRatio: window.devicePixelRatio || 1,
        },
        events: [],
      };
    } else {
      this.currentView = {
        tableauId: parseInt(tableauId),
        sessionId: sessionId,
        visitorId: visitorId,
        startTime: Date.now(),
        interactions: 0,
        scrollDepth: 0,
        cameFrom: document.referrer || window.location.pathname,
        metadata: {
          ...metadata,
          viewport: `${window.innerWidth}x${window.innerHeight}`,
          devicePixelRatio: window.devicePixelRatio || 1,
        },
        events: [],
      };
    }

    this.viewStartTime = Date.now();
    this.interactions = 0;
    this.maxScrollDepth = 0;

    // Démarrer le tracking des interactions
    this.startInteractionTracking();

    console.log(`📊 Vue démarrée pour tableau #${tableauId}`, { sessionId, visitorId });
  }

  /**
   * Tracking des interactions utilisateur sur la page du tableau
   */
  startInteractionTracking() {
    // Nettoyer les anciens listeners s'ils existent
    this.stopInteractionTracking();

    const trackInteraction = (type, event) => {
      if (!this.currentView) return;

      this.interactions++;
      this.currentView.interactions++;

      this.currentView.events.push({
        type,
        timestamp: Date.now() - this.currentView.startTime,
        target: event?.target?.tagName,
        x: event?.clientX,
        y: event?.clientY,
      });

      sessionManager.recordInteraction(type, {
        tableauId: this.currentView.tableauId,
        target: event?.target?.tagName,
      });

      clearTimeout(this.interactionTimer);
      this.interactionTimer = setTimeout(() => {
        this.sendInteractionUpdate();
      }, INTERACTION_DEBOUNCE);
    };

    // Stocker les handlers pour pouvoir les supprimer
    this._clickHandler = (e) => trackInteraction('click', e);
    let lastMouseMove = 0;
    this._mousemoveHandler = (e) => {
      const now = Date.now();
      if (now - lastMouseMove > 1000) {
        trackInteraction('mousemove', e);
        lastMouseMove = now;
      }
    };
    let lastScroll = 0;
    this._scrollHandler = () => {
      const now = Date.now();
      if (now - lastScroll > 500) {
        this.updateScrollDepth();
        lastScroll = now;
      }
    };

    document.addEventListener('click', this._clickHandler, { passive: true });
    document.addEventListener('mousemove', this._mousemoveHandler, { passive: true });
    window.addEventListener('scroll', this._scrollHandler, { passive: true });
  }

  /**
   * Arrête le tracking des interactions et nettoie les listeners
   */
  stopInteractionTracking() {
    if (this._clickHandler) {
      document.removeEventListener('click', this._clickHandler);
      this._clickHandler = null;
    }
    if (this._mousemoveHandler) {
      document.removeEventListener('mousemove', this._mousemoveHandler);
      this._mousemoveHandler = null;
    }
    if (this._scrollHandler) {
      window.removeEventListener('scroll', this._scrollHandler);
      this._scrollHandler = null;
    }
    clearTimeout(this.interactionTimer);
  }

  /**
   * Met à jour la profondeur de scroll
   */
  updateScrollDepth() {
    if (!this.currentView) return;

    const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
    const scrolled = window.scrollY;
    const scrollPercent = scrollHeight > 0 ? (scrolled / scrollHeight) * 100 : 0;

    if (scrollPercent > this.maxScrollDepth) {
      this.maxScrollDepth = Math.min(100, Math.floor(scrollPercent));
      this.currentView.scrollDepth = this.maxScrollDepth;
    }
  }

  /**
   * Envoie une mise à jour des interactions au serveur
   */
  async sendInteractionUpdate() {
    if (!this.currentView) return;

    try {
      await fetch(`${API_BASE_URL}/api/analytics/view-update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tableauId: this.currentView.tableauId,
          sessionId: this.currentView.sessionId,
          interactions: this.currentView.interactions,
          scrollDepth: this.currentView.scrollDepth,
          duration: Math.floor((Date.now() - this.currentView.startTime) / 1000),
        }),
        signal: AbortSignal.timeout(5000),
      });
    } catch (error) {
      // Silencieux
    }
  }

  /**
   * Enregistre une interaction spécifique
   */
  recordInteraction(type, details = {}) {
    if (!this.currentView) return;

    this.interactions++;
    this.currentView.interactions++;

    this.currentView.events.push({
      type,
      timestamp: Date.now() - this.currentView.startTime,
      details,
    });

    sessionManager.recordInteraction(type, {
      tableauId: this.currentView.tableauId,
      ...details,
    });
  }

  /**
   * Termine le tracking de la vue actuelle
   */
  async endView() {
    if (!this.currentView) return;

    // Arrêter le tracking des interactions
    this.stopInteractionTracking();

    const duration = Date.now() - this.currentView.startTime;

    // Ne compter que les vues significatives (> 3 secondes)
    if (duration < VIEW_DURATION_THRESHOLD) {
      console.log(`⏭️ Vue trop courte ignorée (${duration}ms)`);
      this.currentView = null;
      return;
    }

    const viewData = {
      tableauId: this.currentView.tableauId,
      sessionId: this.currentView.sessionId,
      visitorId: this.currentView.visitorId,
      durationSeconds: Math.floor(duration / 1000),
      interactionsCount: this.currentView.interactions,
      scrollDepth: this.currentView.scrollDepth,
      cameFrom: this.currentView.cameFrom,
      metadata: this.currentView.metadata,
      events: this.currentView.events,
      qualityScore: this.calculateQualityScore(duration, this.currentView.interactions, this.currentView.scrollDepth),
    };

    console.log(`✅ Vue terminée pour tableau #${this.currentView.tableauId}`, {
      duration: `${viewData.durationSeconds}s`,
      interactions: viewData.interactionsCount,
      scrollDepth: `${viewData.scrollDepth}%`,
      quality: viewData.qualityScore,
    });

    // Ajouter à la queue
    this.viewQueue.push(viewData);
    this.currentView = null;

    // Traiter la queue
    await this.processViewQueue();
  }

  /**
   * Calcule un score de qualité de la vue (0-100)
   */
  calculateQualityScore(duration, interactions, scrollDepth) {
    // Durée : max 30 points (optimal à 30s+)
    const durationScore = Math.min(30, (duration / 1000 / 30) * 30);

    // Interactions : max 40 points (optimal à 5+ interactions)
    const interactionScore = Math.min(40, (interactions / 5) * 40);

    const scrollScore = (scrollDepth / 100) * 30;

    return Math.round(durationScore + interactionScore + scrollScore);
  }

  /**
   * Traite la queue des vues à envoyer
   */
  async processViewQueue() {
    if (this.isProcessing || this.viewQueue.length === 0) return;

    this.isProcessing = true;

    while (this.viewQueue.length > 0) {
      const viewData = this.viewQueue.shift();

      try {
        await this.sendViewToServer(viewData);
      } catch (error) {
        ErrorHandler.logNetworkError(error, 'ViewTracker.processViewQueue');
        // Remettre dans la queue en cas d'erreur
        this.viewQueue.push(viewData);
        break;
      }
    }

    this.isProcessing = false;
  }

  /**
   * Envoie les données de vue au serveur
   */
  async sendViewToServer(viewData) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/analytics/view`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tableau_id: viewData.tableauId,
          session_id_str: viewData.sessionId,
          visitor_id: viewData.visitorId,
          duration_sec: viewData.durationSeconds,
          interactions_cnt: viewData.interactionsCount,
          scroll_depth_pct: viewData.scrollDepth,
          came_from_url: viewData.cameFrom,
          quality_score: viewData.qualityScore,
          metadata: JSON.stringify(viewData.metadata),
          events: JSON.stringify(viewData.events),
        }),
        signal: AbortSignal.timeout(10000),
      });

      if (!response.ok) {
        throw new Error(`Erreur serveur: ${response.status}`);
      }

      const result = await response.json();
      console.log('📤 Vue envoyée au serveur:', result);

      return result;
    } catch (error) {
      ErrorHandler.logNetworkError(error, 'ViewTracker.sendViewToServer', viewData);
      throw error;
    }
  }

  /**
   * Obtient les statistiques de la vue actuelle
   */
  getCurrentViewStats() {
    if (!this.currentView) return null;

    return {
      tableauId: this.currentView.tableauId,
      duration: Math.floor((Date.now() - this.currentView.startTime) / 1000),
      interactions: this.currentView.interactions,
      scrollDepth: this.currentView.scrollDepth,
      qualityScore: this.calculateQualityScore(
        Date.now() - this.currentView.startTime,
        this.currentView.interactions,
        this.currentView.scrollDepth
      ),
    };
  }

  /**
   * Nettoie les écouteurs d'événements
   */
  cleanup() {
    this.stopInteractionTracking();
    if (this.currentView) {
      this.endView();
    }
  }
}

// Export singleton
const viewTracker = new ViewTracker();

// Nettoyer avant de quitter
window.addEventListener('beforeunload', () => {
  viewTracker.cleanup();
});

export default viewTracker;
